/**
 * Demo mode — a device-local toggle for screen recording. When ON, every
 * site list in the PWA is filtered down to a single tenant so other client
 * names never appear on camera. Purely client-side presentation: the API
 * responses are filtered in endpoints.ts after they arrive, nothing
 * server-side changes, and other devices are unaffected.
 *
 * The scoped tenant defaults to the demo site (Spoke & Sprocket) but can be
 * pointed at any tenant with no UI — open the PWA with ?demoSite=<siteId>
 * (e.g. ?demoSite=toys4vans), which persists the override AND switches demo
 * mode on. ?demoSite=off clears both. Turning demo mode off in Settings also
 * resets the override, so the Settings toggle always means Spoke & Sprocket.
 *
 * Admin-only by UI gating (the Settings toggle only renders for isAdmin);
 * the localStorage flags themselves are harmless if set by anyone — they
 * only HIDE data the operator could already see.
 */

const KEY = 'concierge.demoMode'
const SITE_KEY = 'concierge.demoSite'

/** The default demo tenant's site id (Spoke & Sprocket Cycles). */
export const DEMO_SITE = 'demo'

// URL override — runs once at module load, before any query fires.
try {
  const param = new URLSearchParams(location.search).get('demoSite')
  if (param === 'off') {
    localStorage.removeItem(SITE_KEY)
    localStorage.removeItem(KEY)
  } else if (param) {
    localStorage.setItem(SITE_KEY, param)
    localStorage.setItem(KEY, '1')
  }
} catch {
  // localStorage unavailable — nothing to do
}

/** The tenant demo mode scopes to (override, else the demo site). */
export function demoSite(): string {
  try {
    return localStorage.getItem(SITE_KEY) || DEMO_SITE
  } catch {
    return DEMO_SITE
  }
}

export function isDemoMode(): boolean {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

/** Toggle and reload — a full refresh guarantees every cached query refetches filtered. */
export function setDemoMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, '1')
    else {
      localStorage.removeItem(KEY)
      localStorage.removeItem(SITE_KEY)
    }
  } catch {
    // localStorage unavailable — nothing to do
  }
  location.reload()
}
